/**
 * Amiga output-stage emulation.
 *
 * Paula's DACs feed a fixed analogue reconstruction filter and then an optional
 * second filter switched by the power LED (which is what effect E0x toggles).
 * Reproducing both is most of the difference between "a MOD player" and "sounds
 * like the machine" — the A500's filtering is a big part of why those samples
 * never sounded as harsh as their 8-bit depth suggests.
 */

/** One-pole RC low-pass — the fixed filter on every Amiga's audio output. */
export class OnePole {
  private a = 0;
  private z = 0;

  constructor(cutoffHz: number, sampleRate: number) {
    this.setCutoff(cutoffHz, sampleRate);
  }

  setCutoff(cutoffHz: number, sampleRate: number): void {
    this.a = 1 - Math.exp((-2 * Math.PI * cutoffHz) / sampleRate);
  }

  reset(): void {
    this.z = 0;
  }

  process(x: number): number {
    this.z += this.a * (x - this.z);
    return this.z;
  }
}

/** One-pole high-pass, used for the ~5 Hz DC blocker on the Amiga's output. */
export class OnePoleHigh {
  private a = 0;
  private z = 0;
  private last = 0;

  constructor(cutoffHz: number, sampleRate: number) {
    this.setCutoff(cutoffHz, sampleRate);
  }

  setCutoff(cutoffHz: number, sampleRate: number): void {
    this.a = Math.exp((-2 * Math.PI * cutoffHz) / sampleRate);
  }

  reset(): void {
    this.z = 0;
    this.last = 0;
  }

  process(x: number): number {
    this.z = this.a * this.z + this.a * (x - this.last);
    this.last = x;
    return this.z;
  }
}

/** Transposed direct-form-II biquad. */
export class Biquad {
  private b0 = 1;
  private b1 = 0;
  private b2 = 0;
  private a1 = 0;
  private a2 = 0;
  private z1 = 0;
  private z2 = 0;

  constructor(cutoffHz: number, sampleRate: number, q = Math.SQRT1_2) {
    this.setLowpass(cutoffHz, sampleRate, q);
  }

  setLowpass(cutoffHz: number, sampleRate: number, q = Math.SQRT1_2): void {
    const w0 = (2 * Math.PI * Math.min(cutoffHz, sampleRate * 0.49)) / sampleRate;
    const cos = Math.cos(w0);
    const alpha = Math.sin(w0) / (2 * q);
    const a0 = 1 + alpha;
    this.b0 = ((1 - cos) / 2) / a0;
    this.b1 = (1 - cos) / a0;
    this.b2 = this.b0;
    this.a1 = (-2 * cos) / a0;
    this.a2 = (1 - alpha) / a0;
  }

  reset(): void {
    this.z1 = 0;
    this.z2 = 0;
  }

  process(x: number): number {
    const y = this.b0 * x + this.z1;
    this.z1 = this.b1 * x - this.a1 * y + this.z2;
    this.z2 = this.b2 * x - this.a2 * y;
    return y;
  }
}

export type PaulaModel = 'off' | 'a500' | 'a1200';

/**
 * The full output chain for one stereo side.
 *
 * A500: 6 dB/oct fixed filter around 4.4 kHz, plus the LED filter at 3.3 kHz.
 * A1200: the fixed filter was removed, so only the LED filter remains.
 */
export class PaulaFilter {
  private fixed: OnePole;
  private led1: Biquad;
  private led2: Biquad;
  private dc: OnePoleHigh;
  model: PaulaModel = 'a500';
  ledEnabled = false;

  constructor(sampleRate: number) {
    // R=360Ω, C=0.1µF gives 1/(2πRC) ≈ 4421 Hz.
    this.fixed = new OnePole(4421, sampleRate);
    // The LED filter is a 2-pole Butterworth; cascading two biquads at the
    // Butterworth Q pair gives the 4th-order response the hardware actually has.
    this.led1 = new Biquad(3275, sampleRate, 0.54);
    this.led2 = new Biquad(3275, sampleRate, 1.31);
    this.dc = new OnePoleHigh(5.2, sampleRate);
  }

  setSampleRate(sampleRate: number): void {
    this.fixed.setCutoff(4421, sampleRate);
    this.led1.setLowpass(3275, sampleRate, 0.54);
    this.led2.setLowpass(3275, sampleRate, 1.31);
    this.dc.setCutoff(5.2, sampleRate);
    this.reset();
  }

  reset(): void {
    this.fixed.reset();
    this.led1.reset();
    this.led2.reset();
    this.dc.reset();
  }

  process(x: number): number {
    let y = x;
    if (this.model === 'a500') y = this.fixed.process(y);
    if (this.ledEnabled && this.model !== 'off') {
      y = this.led2.process(this.led1.process(y));
    }
    if (this.model !== 'off') y = this.dc.process(y);
    return y;
  }
}

/**
 * Soft clipper. Linear below the knee so ordinary material is untouched, then a
 * smooth compression curve that never exceeds ±1 — much kinder than letting the
 * output wrap or hard-clip when a dozen channels line up.
 */
export function softClip(x: number, knee = 0.8): number {
  const a = Math.abs(x);
  if (a <= knee) return x;
  const over = (a - knee) / (1 - knee);
  const shaped = knee + (1 - knee) * Math.tanh(over);
  return x < 0 ? -shaped : shaped;
}
